import * as React from 'react';

import {api} from 'nutshell-core/api';
import {Nut_PhoneCaller} from 'nutshell-core/settings-constants';

import GET_SESSION_USER from '../../../core/graphql/queries/get-session-user.graphql';
import {useUpdateSetting} from '../../../core/use-update-setting';

import {nutApolloClient} from '../../../../apollo-client';

type ErrorCode = 'MAX_ATTEMPTS_REACHED' | 'SAVE_SETTING_ERROR';

export type ErrorObject = {
    errorCode: ErrorCode;
    errorMessage: string;
};

type ValidationStatus = {
    isValidated: boolean | null | undefined;
    error: ErrorObject | null | undefined;
};

const MAX_ATTEMPTS = 12;
const POLL_INTERVAL_MS = 5000;

export const useGetClickToCallValidationStatus = (
    phoneNumber: string,
    name: string,
    verificationCode: string
): ValidationStatus => {
    const [numberOfAttempts, setNumberOfAttempts] = React.useState<number>(0);
    const [validationStatus, setValidationStatus] = React.useState<ValidationStatus>({
        isValidated: null,
        error: null,
    });

    const [currentVerificationCode, setCurrentVerificationCode] = React.useState<
        string | null | undefined
    >(null);

    const updateSetting = useUpdateSetting();

    const fetchValidationStatus = React.useCallback((): void => {
        const newNumAttempts = numberOfAttempts + 1;
        setNumberOfAttempts(newNumAttempts);

        if (newNumAttempts > MAX_ATTEMPTS) {
            setValidationStatus({
                isValidated: false,
                error: {
                    errorCode: 'MAX_ATTEMPTS_REACHED',
                    errorMessage:
                        'You did not validate your phone number in time. Not receiving a call? Please try again or contact customer support.',
                },
            });

            return;
        }

        api.get('/phones')
            // @ts-expect-error
            .then((response: Response): Promise<any> => {
                return response.json();
            })
            .then((data: any) => {
                // Only get raw digits of the phone number
                const sanitizedPhoneNumber = phoneNumber.replace(/\D/g, '');

                const previouslyValidatedNumber = data.phones.length
                    ? data.phones[0].e164number.indexOf(sanitizedPhoneNumber) !== -1
                    : false;

                if (previouslyValidatedNumber) {
                    updateSetting({
                        settingName: Nut_PhoneCaller.USER_SETTING_CALLERID_KEY,
                        value: name,
                    })
                        .then(() => {
                            setValidationStatus({
                                isValidated: true,
                                error: null,
                            });

                            nutApolloClient.query({
                                query: GET_SESSION_USER,
                                fetchPolicy: 'network-only',
                            });
                        })
                        .catch(() => {
                            setValidationStatus({
                                isValidated: false,
                                error: {
                                    errorCode: 'SAVE_SETTING_ERROR',
                                    errorMessage: 'An unknown error occurred. Please try again.',
                                },
                            });
                        });
                }
            });
    }, [name, numberOfAttempts, phoneNumber, updateSetting]);

    React.useEffect(() => {
        if (currentVerificationCode !== verificationCode) {
            setCurrentVerificationCode(verificationCode);
            setNumberOfAttempts(0);
            setValidationStatus({
                isValidated: null,
                error: null,
            });
        }
    }, [currentVerificationCode, verificationCode]);

    React.useEffect(() => {
        if (validationStatus.isValidated !== null) {
            return;
        }

        const interval = setInterval(() => {
            fetchValidationStatus();
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [fetchValidationStatus, validationStatus.isValidated]);

    return validationStatus;
};
